import { Component, inject, Input, OnInit } from "@angular/core";
import { SubmitInfo } from "@app/core/domain/submit-proposal";
import {
  SubmitProposalStateService,
  SubmitProposalStep,
} from "@app/features/submit-proposal/services/submit-proposal-state.service";
import { PlanStateService } from "@app/shared/services/plan-state.service";
import { Constants } from "@core/constants";
import { PlanService } from "@core/services/plan.service";
import {
  IonButton,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
  ModalController,
} from "@ionic/angular/standalone";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { SubmitProposalContentComponent } from "../submit-proposal-content/submit-proposal-content.component";

@Component({
  selector: "pa-submit-proposal-modal",
  templateUrl: "./submit-proposal-modal.component.html",
  styleUrls: ["./submit-proposal-modal.component.scss"],
  imports: [
    SubmitProposalContentComponent,
    TranslatePipe,
    IonHeader,
    IonToolbar,
    IonButton,
    IonTitle,
    IonIcon,
    IonContent,
    IonFooter,
  ],
})
export class SubmitProposalModalComponent implements OnInit {
  @Input() submitInfo: SubmitInfo | undefined = undefined;

  readonly Constants = Constants;
  readonly SubmitProposalStep = SubmitProposalStep;
  currentStep: SubmitProposalStep = SubmitProposalStep.Choice;
  title = "";
  private readonly planService = inject(PlanService);
  private readonly planStateService = inject(PlanStateService);
  private readonly submitProposalStateService = inject(SubmitProposalStateService);
  private readonly modalCtrl = inject(ModalController);
  private readonly translate = inject(TranslateService);

  ngOnInit(): void {
    this.submitProposalStateService.initSubmitInfo(this.submitInfo!, this.currentStep);
    this.determineTitle();
  }

  dismiss(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, Constants.BUTTON_ACTION_CANCEL);
  }

  private changeStepTo(step: SubmitProposalStep): void {
    this.currentStep = step;
    this.determineTitle();
    this.submitProposalStateService.changeStepTo(step);
  }

  backPressed(): void {
    this.changeStepTo(SubmitProposalStep.Choice);
  }

  nextPressed(): void {
    this.changeStepTo(SubmitProposalStep.Confirm);
  }

  isNextButtonDisabled(): boolean {
    return !this.submitProposalStateService.isNextStepAllowed();
  }

  isSubmitButtonDisabled(): boolean {
    return this.submitProposalStateService.submitDisabled;
  }

  submitPressed(): void {
    this.submitProposalStateService.submitProposal().subscribe((result) => {
      if (result) {
        this.modalCtrl.dismiss(null, Constants.BUTTON_ACTION_OK).catch((error) => {
          console.error("Error dismissing submit proposal modal while pressing submit:", error);
        });

        // Reload the submitted plan when submit is successful
        if (this.submitInfo) {
          this.planService.getOrFetchPlanDetailsAndPlanLater(this.submitInfo.planId, true).subscribe();
        }
      }
    });
  }

  private determineTitle(): void {
    const paramProposalName = this.planStateService.getParamProposalName();
    this.title =
      this.currentStep === SubmitProposalStep.Confirm
        ? (this.translate.instant("submitProposal.confirmTitle", paramProposalName) as string)
        : (this.translate.instant("submitProposal.choiceTitle", paramProposalName) as string);
  }
}
